import { useState, useRef, useCallback } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Wordmark from '../components/Brand';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import * as authService from '../services/authService';

const TOKEN_KEY = 'admin_access_token';
const REFRESH_KEY = 'admin_refresh_token';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const idleReason = searchParams.get('reason') === 'idle';
  const { isAuthenticated } = useAuthStore();

  const [step, setStep] = useState<'credentials' | 'totp'>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authService.login(email, password);

      // Check if 2FA is required
      const data = result as unknown as authService.TotpLoginResponse;
      if (data.requireTotp && data.tempToken) {
        setTempToken(data.tempToken);
        setStep('totp');
        setLoading(false);
        return;
      }

      // Normal login (no 2FA)
      localStorage.setItem(TOKEN_KEY, result.tokens.accessToken);
      localStorage.setItem(REFRESH_KEY, result.tokens.refreshToken);
      const me = await authService.getMe();
      if (me.role !== 'SUPER_ADMIN') {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        throw new Error('운영 관리 권한이 없습니다.');
      }
      useAuthStore.setState({
        user: { id: me.id, email: me.email, name: me.name, role: me.role },
        isAuthenticated: true,
      });
      toast.success('로그인되었습니다.');
      navigate('/', { replace: true });
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.message ??
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
            '로그인에 실패했습니다.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleTotpComplete = async (code: string) => {
    setError('');
    setLoading(true);
    try {
      const result = await authService.totpAuthenticate({ tempToken, totpToken: code });
      localStorage.setItem(TOKEN_KEY, result.tokens.accessToken);
      localStorage.setItem(REFRESH_KEY, result.tokens.refreshToken);
      const me = await authService.getMe();
      if (me.role !== 'SUPER_ADMIN') {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        throw new Error('운영 관리 권한이 없습니다.');
      }
      useAuthStore.setState({
        user: { id: me.id, email: me.email, name: me.name, role: me.role },
        isAuthenticated: true,
      });
      toast.success('로그인되었습니다.');
      navigate('/', { replace: true });
    } catch (err) {
      const errData = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      setError(errData?.error === 'INVALID_TOTP' ? '잘못된 인증 코드입니다.' : errData?.message ?? '인증에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/favicon.svg" alt="MaioS" className="w-14 h-14 mx-auto mb-4" />
          <h1 className="text-2xl tracking-tight">
            <Wordmark className="text-2xl" /> <span className="font-bold text-gray-900">Admin</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">운영 관리 시스템</p>
        </div>

        {idleReason && step === 'credentials' && (
          <div className="bg-yellow-50 text-yellow-700 p-3 rounded-lg mb-4 text-sm">
            세션이 만료되었습니다. 다시 로그인해주세요.
          </div>
        )}

        {step === 'credentials' ? (
          <div className="bg-white shadow-lg rounded-2xl p-8">
            <form onSubmit={handleCredentialsSubmit} className="space-y-5">
              <Input id="email" label="이메일" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="admin@maios.local"
                autoComplete="email" autoFocus required />
              <Input id="password" label="비밀번호" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="비밀번호를 입력하세요"
                autoComplete="current-password" required />
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              <Button type="submit" loading={loading} disabled={!email || !password} className="w-full">
                로그인
              </Button>
            </form>
            <p className="mt-6 text-center text-sm text-gray-400">SUPER_ADMIN 권한이 필요합니다</p>
          </div>
        ) : (
          <div className="bg-white shadow-lg rounded-2xl p-8">
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">2단계 인증</h2>
            <p className="text-sm text-gray-600 text-center mb-6">인증 앱의 6자리 코드를 입력하세요</p>
            <TotpInput onComplete={handleTotpComplete} disabled={loading} error={error} />
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg mt-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
            <button onClick={() => { setStep('credentials'); setError(''); }}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mt-4 mx-auto">
              <ArrowLeft className="size-4" /> 뒤로 가기
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TotpInput({ onComplete, disabled, error }: { onComplete: (code: string) => void; disabled: boolean; error: string }) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const resetInputs = useCallback(() => {
    setDigits(Array(6).fill(''));
    inputRefs.current[0]?.focus();
  }, []);

  // Reset on error change
  const prevError = useRef(error);
  if (error && error !== prevError.current) {
    prevError.current = error;
    setTimeout(resetInputs, 100);
  }

  const handleChange = (idx: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[idx] = value;
    setDigits(next);

    if (value && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }

    // Auto-submit when complete
    if (value && idx === 5) {
      const code = next.join('');
      if (code.length === 6) onComplete(code);
    } else if (value) {
      const code = next.join('');
      if (code.length === 6) onComplete(code);
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 0) return;
    const next = [...digits];
    for (let i = 0; i < pasted.length && i < 6; i++) next[i] = pasted[i];
    setDigits(next);
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs.current[focusIdx]?.focus();
    if (pasted.length === 6) onComplete(pasted);
  };

  return (
    <div className="flex justify-center gap-2" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]"
          maxLength={1}
          value={d}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          disabled={disabled}
          autoFocus={i === 0}
          className="w-12 h-14 text-center text-xl font-mono border-2 border-gray-300 rounded-lg
            focus:border-gray-900 focus:outline-none transition-colors disabled:opacity-50"
        />
      ))}
    </div>
  );
}
